import { Button, Spinner } from "@heroui/react";
import { PressableFeedback } from "@heroui-pro/react/pressable-feedback";
import { AnimatePresence, m, useReducedMotion } from "framer-motion";
import { useId } from "react";
import { easings } from "@funnel-ui/motion";

type Gate = { progress: number; label?: string };

type ContinueButtonProps = {
  label?: string;
  onPress?: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  gate?: Gate;
  skip?: { label: string; onPress: () => void };
  fullWidth?: boolean;
  className?: string;
  testId?: string;
};

const resolveDisplayLabel = (label: string, gate: Gate | undefined, gated: boolean): string => {
  if (!gated) return label;
  return gate?.label ?? `Continue (${Math.ceil((gate?.progress ?? 0) * 100)}%)`;
};

const resolveStatusMessage = (
  isLoading: boolean | undefined,
  gated: boolean,
  gate: Gate | undefined,
): string => {
  if (isLoading) return "Working...";
  if (gated) {
    return `Preparing — ${Math.ceil((gate?.progress ?? 0) * 100)}% ready`;
  }
  return "Ready to continue";
};

const GateOverlay = ({ gate, gated }: { gate: Gate | undefined; gated: boolean }) => {
  if (!gated) return null;
  return (
    <span
      aria-hidden="true"
      className="absolute inset-y-0 left-0"
      style={{
        width: `${(gate?.progress ?? 0) * 100}%`,
        background: "hsl(var(--brand-primary-foreground) / 0.18)",
        transition: "width 280ms cubic-bezier(0.32,0.72,0,1)",
      }}
    />
  );
};

const ButtonLabel = ({
  label,
  showSpinner,
  prefersReduced,
}: {
  label: string;
  showSpinner: boolean;
  prefersReduced: boolean;
}) => (
  <span className="relative z-10 inline-flex items-center justify-center gap-2">
    {showSpinner && <Spinner aria-hidden="true" color="current" size="sm" />}
    <AnimatePresence initial={false} mode="popLayout">
      <m.span
        key={label}
        initial={{ opacity: 0, y: prefersReduced ? 0 : 4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: prefersReduced ? 0 : -4 }}
        transition={{
          duration: prefersReduced ? 0 : 0.16,
          ease: easings.emphasized,
        }}
      >
        {label}
      </m.span>
    </AnimatePresence>
  </span>
);

export const ContinueButton = ({
  label = "Continue",
  onPress,
  disabled,
  isLoading,
  gate,
  skip,
  fullWidth = true,
  className,
  testId,
}: ContinueButtonProps) => {
  const gated = !!gate && gate.progress < 1;
  const effDisabled = disabled || gated || isLoading;
  const prefersReduced = useReducedMotion();
  const statusId = useId();
  const displayLabel = resolveDisplayLabel(label, gate, gated);
  const statusMessage = resolveStatusMessage(isLoading, gated, gate);

  return (
    <div
      className={`flex w-full flex-col items-center gap-2.5 ${className ?? ""}`}
      data-testid={testId}
    >
      <Button
        variant="primary"
        size="lg"
        fullWidth={fullWidth}
        onPress={onPress}
        isDisabled={effDisabled}
        isPending={isLoading}
        aria-disabled={effDisabled}
        aria-describedby={statusId}
        data-gated={gated || undefined}
        data-editable-path="ctaLabel"
        className="relative h-13 max-w-[480px] overflow-hidden rounded-full font-semibold tracking-tight"
      >
        <PressableFeedback.Ripple />
        <GateOverlay gate={gate} gated={gated} />
        <ButtonLabel
          label={displayLabel}
          showSpinner={!!isLoading}
          prefersReduced={!!prefersReduced}
        />
      </Button>
      <span id={statusId} className="sr-only" role="status" aria-live="polite">
        {statusMessage}
      </span>
      {skip && (
        <Button
          variant="ghost"
          size="sm"
          onPress={skip.onPress}
          className="underline underline-offset-[3px] opacity-70"
        >
          {skip.label}
        </Button>
      )}
    </div>
  );
};

export default ContinueButton;
